import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { ConfirmResult } from "@ryde/shared";

import { useModalConfirm } from "../../hooks-singletons";

const SHIFT_TIME_FIELDS = ["shiftStartTime", "shiftEndTime"] as const;

/**
 * Shift-specific save helper: when a shift's start/end time was edited, asks the admin whether
 * future "New" bookings should be retimed to the new hours. The shift itself still saves either way —
 * only the booking propagation is opt-in. Callers gate this by entity (see useEditFormInner).
 */
export const useShiftTimeChangeConfirm = () => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();

  const confirmUpdateFutureBookings = useEvent(
    async ({
      isEdit,
      dirtyFields,
    }: {
      isEdit: boolean;
      dirtyFields: Record<string, unknown>;
    }): Promise<boolean> => {
      const hasShiftTimeChange =
        isEdit && SHIFT_TIME_FIELDS.some(field => Boolean(dirtyFields[field]));
      if (!hasShiftTimeChange) {
        return false;
      }

      const result = await confirmDialogResult({
        title: t("shift.UPDATE_FUTURE_BOOKINGS_TITLE"),
        message: t("shift.UPDATE_FUTURE_BOOKINGS_MESSAGE"),
        okButtonTitle: t("general.YES"),
        cancelButtonTitle: t("general.NO"),
      });

      return result === ConfirmResult.OK;
    },
  );

  return { confirmUpdateFutureBookings };
};
